import { ColorModel } from '@tableau/color-and-border-models';
import * as React from 'react';
import { ButtonZoneImageStyle } from '../styles/ButtonZoneImageStyle';

export const TabButtonZoneSVGImageWrapperClass = 'tab-button-zone-SVG-image-wrapper';
export const TabButtonZoneImageClass = 'tab-button-zone-image';
export const TabButtonZoneImageOffLineAppender = '-offline';
export const TabButtonZoneImageComponentTestId = 'tab-button-zone-image-enabled-container';

export interface ButtonZoneImageProps {
  readonly imageUrl: string;
  readonly encodedImage: string;
  readonly encodedSVGImage: string;
  readonly imageHeight: number | string;
  readonly imageWidth: number | string;
  readonly backgroundColor: string;
  readonly hoverActive: boolean;
  readonly isButtonImageEnabled: boolean;
  readonly onImageLoadError?: () => void;
  readonly vizqlRoutingHeaderName?: string;
  readonly vizqlRoutingKey?: string;
}

interface ButtonZoneImageState {
  // When on Hyperforce, this stores the image blob from the server
  hyperforceImageSource?: string;
}

export class ButtonZoneImageComponent extends React.PureComponent<ButtonZoneImageProps, ButtonZoneImageState> {
  public constructor(props: ButtonZoneImageProps) {
    super(props);
  }

  private isInHyperforce(): boolean {
    return !!this.props.vizqlRoutingHeaderName && !!this.props.vizqlRoutingKey;
  }

  private async fetchImage(srcAtrr: string, routingHeaderName: string, routingKey: string): Promise<string> {
    const result = await fetch(srcAtrr, {
      headers: {
        [routingHeaderName]: routingKey,
      },
    });

    const inputBlob = await result.blob();
    const blobAsUrl = URL.createObjectURL(inputBlob);
    return blobAsUrl;
  }

  private setHyperforceImageStateIfApplicable() {
    if (!this.isInHyperforce()) {
      return;
    }

    void this.fetchImage(this.props.imageUrl, this.props.vizqlRoutingHeaderName || '', this.props.vizqlRoutingKey || '')
      .then((newSource: string) => {
        this.setState({ hyperforceImageSource: newSource });
      })
      .catch((e) => {
        console.log(e);
      });
  }

  componentDidMount() {
    this.setHyperforceImageStateIfApplicable();
  }

  componentDidUpdate(prevProps: Readonly<ButtonZoneImageProps>, _prevState: Readonly<ButtonZoneImageState>, _snapshot?: any): void {
    if (this.props.imageUrl !== prevProps.imageUrl) {
      this.setHyperforceImageStateIfApplicable();
    }
  }

  componentWillUnmount() {
    this.state?.hyperforceImageSource && URL.revokeObjectURL(this.state.hyperforceImageSource);
  }

  public render(): JSX.Element {
    if (this.props.isButtonImageEnabled) {
      return (
        <div className={ButtonZoneImageStyle.tabButtonZoneImageEnabledContainer} data-testid={TabButtonZoneImageComponentTestId}>
          {this.renderImageElement(TabButtonZoneImageClass)}
          {this.props.hoverActive ? this.renderButtonZoneImageHover() : null}
        </div>
      );
    } else {
      return this.renderImageElement(ButtonZoneImageStyle.tabButtonZoneImageDisabled);
    }
  }

  private renderImageElement(buttonImageClassName: string): JSX.Element {
    const imageWidth = this.props.imageWidth;
    const imageHeight = this.props.imageHeight;
    let imagePath: string | undefined = this.props.imageUrl;

    if (this.props.encodedImage.length > 0) {
      const offlineImageClassName = buttonImageClassName + TabButtonZoneImageOffLineAppender;
      return this.renderOfflineImage(imageWidth, imageHeight, offlineImageClassName);
    }

    if (this.props.encodedSVGImage.length > 0) {
      imagePath = 'data:image/svg+xml;base64,' + this.props.encodedSVGImage;
    } else if (this.isInHyperforce()) {
      // Use the generated blob if we have finished loading it; otherwise leave the image blank
      imagePath = this.state?.hyperforceImageSource || undefined;
    }

    return this.createSVGWrapperForImage(imageWidth, imageHeight, imagePath, buttonImageClassName);
  }

  private renderOfflineImage(imageWidth: number | string, imageHeight: number | string, offlineImageClassName: string): JSX.Element {
    const imagePath = 'data:image/jpeg;base64,' + this.props.encodedImage;
    return <img className={offlineImageClassName} src={imagePath} width={imageWidth} height={imageHeight} alt={''} />;
  }

  private createSVGWrapperForImage(
    imageWidth: number | string,
    imageHeight: number | string,
    imagePath: string | undefined,
    buttonImageClassName: string
  ): JSX.Element {
    // If imagePath is blank, that means we're waiting on the blob with server attributes.
    // In that case, we still want to make space for the icon, but we skip the image itself.
    return (
      <svg className={TabButtonZoneSVGImageWrapperClass} width={imageWidth} height={imageHeight}>
        <defs>
          <filter id="grayscale">
            <feColorMatrix
              values="0.3333 0.3333 0.3333 0.4 0
                      0.3333 0.3333 0.3333 0.4 0
                      0.3333 0.3333 0.3333 0.4 0
                      0      0      0      1   0"
            />
          </filter>
        </defs>
        {imagePath && (
          <image
            className={buttonImageClassName}
            href={imagePath}
            width={imageWidth}
            height={imageHeight}
            onError={this.props.onImageLoadError}
          />
        )}
      </svg>
    );
  }

  private renderButtonZoneImageHover(): JSX.Element {
    const hoverStyle: React.CSSProperties = {
      width: this.props.imageWidth,
      height: this.props.imageHeight,
      backgroundColor: ColorModel.fromColorCode(this.props.backgroundColor).toRGB(),
      opacity: 0.5,
    };

    return <div className={ButtonZoneImageStyle.tabButtonZoneImageHover} style={hoverStyle}></div>;
  }
}
